import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { SectionTitle, Loading, StatCard } from "@/components/common";
import { DollarSign, Percent, Award, Target, RefreshCw, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const _esc = (s) => String(s).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));

export default function FinancialAnalytics() {
  const [f, setF] = useState(null);
  const [audit, setAudit] = useState([]);
  useEffect(() => {
    api.get("/financial/analytics").then(({ data }) => setF(data));
    api.get("/audit-logs", { params: { limit: 40 } }).then(({ data }) => setAudit(data));
  }, []);
  if (!f) return <Loading label="Loading financials" />;

  const t = f.targets.six_figure_standard;

  return (
    <div className="max-w-6xl">
      <SectionTitle sub="Owner-only revenue, discounts & audit trail">Financial Analytics</SectionTitle>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total Collected" value={fmtMoney(f.total_collected)} accent icon={DollarSign} testid="fa-collected" />
        <StatCard label="Effective Discount" value={`${f.effective_discount_pct}%`} icon={Percent} testid="fa-disc" />
        <StatCard label="Active Memberships" value={f.membership_active} icon={Award} testid="fa-mem" />
        <StatCard label="Annual Benchmark" value={`$${(t.annual / 1000)}k`} icon={Target} testid="fa-annual" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="sg-card p-5" data-testid="fa-membership">
          <div className="font-head text-lg font-bold mb-3">Membership Program</div>
          <div className="flex flex-col gap-2 text-sm">
            <Row l="Monthly recurring revenue" v={`$${f.membership_monthly_revenue}`} />
            <Row l="Sales bonus (to seller)" v={`${Math.round(f.membership_sales_bonus_pct * 100)}% / 30 days`} />
            <Row l="Quoted list total" v={fmtMoney(f.list_total)} />
            <Row l="Quoted final total" v={fmtMoney(f.final_total)} />
          </div>
          <div className="text-xs mt-3" style={{ color: "var(--muted)" }}>Shadow Guide is not the legal payroll system. Payroll processed via approved accounting systems.</div>
        </div>

        <div className="sg-card p-5 lg:col-span-2" data-testid="fa-audit">
          <div className="font-head text-lg font-bold mb-3">Audit Log (recent)</div>
          <div className="flex flex-col gap-1 max-h-96 overflow-y-auto">
            {audit.map((a) => (
              <div key={a.id} className="flex items-center justify-between text-xs font-mono py-1 border-b" style={{ borderColor: "var(--brd)" }}>
                <span><span style={{ color: "var(--accent)" }}>{a.action}</span> · {a.entity} · {a.user_name}</span>
                <span style={{ color: "var(--muted)" }}>{new Date(a.created_at).toLocaleTimeString()}</span>
              </div>
            ))}
            {audit.length === 0 && <div className="text-sm" style={{ color: "var(--txt2)" }}>No audit entries yet.</div>}
          </div>
        </div>
      </div>

      <MembershipRevenueReport />
      <SquareOps />
    </div>
  );
}

function SquareOps() {
  const [disp, setDisp] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const loadDisputes = async () => { try { const { data } = await api.get("/disputes"); setDisp(data); } catch { setDisp({ disputes: [], open_count: 0 }); } };
  useEffect(() => { loadDisputes(); }, []);

  const reconcile = async (dry) => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/square/reconcile", { dry_run: dry });
      setResult(data);
      toast.success(data.in_sync ? "Shadow Guide is in sync with Square" : `${dry ? "Preview" : "Reconciled"}: ${data.corrected_count} corrected, ${data.difference_count} to review`);
      await loadDisputes();
    } catch (e) { toast.error(e.response?.data?.detail || "Reconcile failed"); }
    finally { setBusy(false); }
  };

  const stateTone = (s) => (s === "WON" ? "#10B981" : ["LOST", "ACCEPTED"].includes(s) ? "#EF4444" : "#F59E0B");
  const [evidence, setEvidence] = useState(null);
  const [evLoading, setEvLoading] = useState(false);

  const openEvidence = async (d) => {
    setEvLoading(true); setEvidence({ loading: true, dispute: d });
    try { const { data } = await api.get(`/disputes/${d.id}/evidence`); setEvidence(data); }
    catch { toast.error("Could not load evidence"); setEvidence(null); }
    finally { setEvLoading(false); }
  };
  const exportEvidence = () => {
    const blob = new Blob([JSON.stringify(evidence, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `dispute-evidence-${evidence?.dispute?.square_dispute_id || "export"}.json`; a.click();
    URL.revokeObjectURL(url);
  };
  const exportEvidencePDF = () => {
    if (!evidence || evidence.loading) return;
    const d = evidence.dispute || {};
    const rows = (evidence.evidence || []).map((e) => `<tr>
      <td style="padding:6px 8px;border-bottom:1px solid #ddd;vertical-align:top;width:220px"><b>${_esc(e.label)}</b><br><span style="color:${e.present ? "#059669" : "#b45309"};font-size:11px;font-weight:bold">${e.present ? "PRESENT" : "MISSING"}</span></td>
      <td style="padding:6px 8px;border-bottom:1px solid #ddd;font-family:monospace;font-size:11px;white-space:pre-wrap;word-break:break-word">${e.present ? _esc(typeof e.value === "object" ? JSON.stringify(e.value, null, 1) : String(e.value)) : "—"}</td></tr>`).join("");
    const win = window.open("", "_blank");
    win.document.write(`<html><head><title>Dispute Evidence — ${_esc(d.square_dispute_id || "")}</title></head>
      <body style="font-family:Arial,sans-serif;padding:24px;color:#111">
      <h1 style="color:#b91c1c;margin:0 0 8px">FreshSurf — Dispute Evidence Package</h1>
      <p style="margin:2px 0">Customer: <b>${_esc(evidence.customer_name || "(no job link)")}</b></p>
      <p style="margin:2px 0">Square dispute: <b>${_esc(d.square_dispute_id || "")}</b> · State: <b>${_esc(d.state || "")}</b> · Amount: <b>${fmtMoney(d.amount)}</b></p>
      <p style="margin:2px 0">Response deadline: <b>${_esc(d.deadline || "Not provided by Square")}</b></p>
      <p style="margin:2px 0">Job: <b>${_esc(d.job_id || "—")}</b> · Payment ref: <b>${_esc(d.square_payment_id || "—")}</b></p>
      <p style="margin:2px 0;color:#555">Generated ${new Date().toLocaleString()} · Present ${evidence.present_count} · Missing ${evidence.missing_count}</p>
      <table style="width:100%;border-collapse:collapse;margin-top:12px"><thead><tr style="text-align:left"><th style="padding:6px 8px;border-bottom:2px solid #111">Evidence</th><th style="padding:6px 8px;border-bottom:2px solid #111">Detail</th></tr></thead><tbody>${rows}</tbody></table>
      <p style="font-size:11px;color:#888;margin-top:16px">${_esc(evidence.note || "")}</p>
      <script>window.onload=function(){setTimeout(function(){window.print();},200);}<\/script></body></html>`);
    win.document.close();
  };

  return (
    <div className="sg-card p-5 mt-6" data-testid="square-ops">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div className="font-head text-lg font-bold">Square Operations</div>
        <div className="flex gap-2">
          <button className="sg-btn" style={{ minHeight: 40 }} disabled={busy} onClick={() => reconcile(true)} data-testid="reconcile-preview-btn"><RefreshCw size={15} /> Preview</button>
          <button className="sg-btn sg-btn-accent" style={{ minHeight: 40 }} disabled={busy} onClick={() => reconcile(false)} data-testid="reconcile-btn"><RefreshCw size={15} /> {busy ? "Working…" : "Reconcile with Square"}</button>
        </div>
      </div>

      {result && (
        <div className="mb-4 p-3 rounded-lg" style={{ background: "var(--bg-elev)" }} data-testid="reconcile-result">
          <div className="text-sm mb-2">
            Checked — payments {result.checked.payments}, refunds {result.checked.refunds}, subscriptions {result.checked.subscriptions}, disputes {result.checked.disputes}
            {result.dry_run && <span className="chip ml-2" style={{ fontSize: 10 }}>DRY RUN</span>}
          </div>
          {result.in_sync ? (
            <div className="text-sm" style={{ color: "#10B981" }} data-testid="reconcile-insync">✓ Everything is in sync with Square.</div>
          ) : (
            <>
              {[...result.corrections.map((c) => ({ ...c, applied: true })), ...result.differences.map((c) => ({ ...c, applied: false }))].map((c, i) => (
                <div key={i} className="text-xs py-1" data-testid={`reconcile-item-${i}`}>
                  <span className="chip mr-2" style={{ fontSize: 10, color: c.applied ? "#10B981" : "#F59E0B", borderColor: c.applied ? "#10B981" : "#F59E0B" }}>{c.applied ? "CORRECTED" : "REVIEW"}</span>
                  <b>{c.type}</b> {c.id} — {c.detail}
                </div>
              ))}
            </>
          )}
          <div className="text-[11px] mt-2" style={{ color: "var(--muted)" }}>{result.note}</div>
        </div>
      )}

      <div className="flex items-center gap-2 mb-2">
        <ShieldAlert size={16} style={{ color: disp?.open_count ? "#EF4444" : "var(--txt2)" }} />
        <span className="font-head font-bold">Disputes / Chargebacks</span>
        {disp?.open_count > 0 && <span className="chip" style={{ fontSize: 10, color: "#EF4444", borderColor: "#EF4444" }} data-testid="disputes-open-count">{disp.open_count} open</span>}
      </div>
      {(!disp || disp.disputes.length === 0) && <div className="text-sm" style={{ color: "var(--muted)" }} data-testid="disputes-empty">No disputes on record.</div>}
      {disp?.disputes.map((d) => (
        <div key={d.id} className="text-sm py-1 flex items-center gap-2" data-testid={`dispute-row-${d.square_dispute_id}`} style={{ borderTop: "1px solid var(--brd)" }}>
          <span className="chip" style={{ fontSize: 10, color: stateTone(d.state), borderColor: stateTone(d.state) }}>{d.state}</span>
          <span style={{ color: "var(--txt)" }}>{d.customer_name || "(no job link)"}</span>
          <span className="font-mono" style={{ color: "var(--txt2)" }}>{fmtMoney(d.amount)}</span>
          {d.reason && <span className="text-xs" style={{ color: "var(--muted)" }}>· {d.reason}</span>}
          {d.reversed && <span className="text-xs" style={{ color: "#EF4444" }}>· reversed from revenue</span>}
          <button className="sg-btn ml-auto" style={{ minHeight: 30, padding: "3px 10px" }} disabled={evLoading} onClick={() => openEvidence(d)} data-testid={`dispute-evidence-btn-${d.square_dispute_id}`}>Evidence</button>
        </div>
      ))}
      <div className="text-[11px] mt-2" style={{ color: "var(--muted)" }}>Square is authoritative for dispute state. Disputed funds are excluded from revenue/KPIs and never trigger payroll or production bonuses.</div>

      {evidence && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }} onClick={() => setEvidence(null)}>
          <div className="sg-card p-6 w-full max-w-2xl" style={{ maxHeight: "85vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()} data-testid="dispute-evidence-modal">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-head text-xl font-bold">Dispute Evidence Package</h3>
              <button onClick={() => setEvidence(null)} data-testid="evidence-close">✕</button>
            </div>
            {evidence.loading ? <div className="text-sm" style={{ color: "var(--muted)" }}>Gathering evidence…</div> : (
              <>
                <div className="text-sm mb-3" style={{ color: "var(--txt2)" }}>
                  {evidence.customer_name || "(no job link)"} · <span style={{ color: "#10B981" }}>{evidence.present_count} present</span> · <span style={{ color: "#F59E0B" }}>{evidence.missing_count} missing</span>
                </div>
                {evidence.evidence?.map((e, i) => (
                  <div key={i} className="py-2" style={{ borderTop: "1px solid var(--brd)" }} data-testid={`evidence-item-${i}`}>
                    <div className="flex items-center gap-2">
                      <span className="chip" style={{ fontSize: 10, color: e.present ? "#10B981" : "#F59E0B", borderColor: e.present ? "#10B981" : "#F59E0B" }}>{e.present ? "PRESENT" : "MISSING"}</span>
                      <b>{e.label}</b>
                    </div>
                    {e.present && <pre className="text-[11px] mt-1 whitespace-pre-wrap break-words" style={{ color: "var(--txt2)", maxHeight: 140, overflowY: "auto" }}>{typeof e.value === "object" ? JSON.stringify(e.value, null, 1) : String(e.value)}</pre>}
                  </div>
                ))}
                <div className="text-[11px] mt-3" style={{ color: "var(--muted)" }}>{evidence.note}</div>
                <div className="flex gap-2 mt-4">
                  <button className="sg-btn sg-btn-accent" style={{ minHeight: 40 }} onClick={exportEvidencePDF} data-testid="evidence-export-pdf">Export PDF for Owner Review</button>
                  <button className="sg-btn" style={{ minHeight: 40 }} onClick={exportEvidence} data-testid="evidence-export">Export JSON</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MembershipRevenueReport() {
  const [r, setR] = useState(null);
  useEffect(() => { api.get("/memberships/revenue-report", { params: { months: 6 } }).then(({ data }) => setR(data)).catch(() => setR(null)); }, []);
  if (!r) return null;
  const maxNew = Math.max(1, ...r.monthly.map((m) => m.new));
  return (
    <div className="sg-card p-5 mt-6" data-testid="membership-revenue-report">
      <div className="flex items-center justify-between mb-3">
        <div className="font-head text-lg font-bold">Membership Recurring Revenue</div>
        {r.sandbox && <span className="chip" style={{ fontSize: 10 }} data-testid="mrr-sandbox-tag">SANDBOX · excluded from live KPIs</span>}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
        <StatCard label="MRR" value={fmtMoney(r.mrr)} accent testid="mrr-mrr" />
        <StatCard label="Collected (confirmed)" value={fmtMoney(r.collected_confirmed)} testid="mrr-collected" />
        <StatCard label="Active" value={r.active_count} testid="mrr-active" />
        <StatCard label="New (this month)" value={r.new_this_month} testid="mrr-new" />
        <StatCard label="Failed Payments" value={r.failed_count} testid="mrr-failed" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <div className="text-xs uppercase tracking-wider mb-2" style={{ color: "var(--txt2)" }}>Tier breakdown</div>
          {r.tier_breakdown.length === 0 && <div className="text-sm" style={{ color: "var(--muted)" }}>No active memberships.</div>}
          {r.tier_breakdown.map((t) => (
            <Row key={t.tier} l={t.label} v={`${t.count}`} />
          ))}
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider mb-2" style={{ color: "var(--txt2)" }}>New vs cancellations (month-over-month)</div>
          {r.monthly.map((m) => (
            <div key={m.month} className="flex items-center gap-2 mb-1 text-xs" data-testid={`mrr-month-${m.month}`}>
              <span className="font-mono" style={{ width: 64, color: "var(--txt2)" }}>{m.month}</span>
              <div className="flex-1 h-3 rounded" style={{ background: "var(--bg-elev)" }}>
                <div style={{ width: `${(m.new / maxNew) * 100}%`, height: "100%", background: "var(--accent)", borderRadius: 4 }} />
              </div>
              <span className="font-mono" style={{ color: "#10B981" }}>+{m.new}</span>
              <span className="font-mono" style={{ color: "#EF4444" }}>-{m.canceled}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="text-[11px] mt-3" style={{ color: "var(--muted)" }}>{r.note}</div>
    </div>
  );
}

function Row({ l, v }) {
  return <div className="flex items-center justify-between"><span style={{ color: "var(--txt2)" }}>{l}</span><span className="font-mono font-bold">{v}</span></div>;
}
